import type { ComputedRef, Ref } from 'vue'
import type GlobalSearchBar from '~/components/search/GlobalSearchBar.vue'
import { useShortcutKeyVisualFeedback } from '~/composables/ui/useShortcutKeyVisualFeedback'
import { shortcutMatchesEvent, useUserShortcuts } from '~/composables/ui/useUserShortcuts'
import { useUserPreferences } from '~/composables/ui/useUserPreferences'
import { isApplePlatform } from '~/utils/isApplePlatform'

type UseAppLayoutKeyboardShortcutsParams = {
  loggedIn: Ref<boolean>
  isConnectionRoute: Ref<boolean>
  isEditing: Ref<boolean>
  trySetEditMode: (value: boolean) => void
  toolboxShortcutsEnabled: ComputedRef<boolean>
  onToolboxToggle: () => void
  onGeolocate: () => void
}

function shouldFocusGlobalSearchFromShortcut(event: KeyboardEvent, loggedIn: boolean, isConnectionRoute: boolean): boolean {
  if (!loggedIn || isConnectionRoute) {
    return false
  }
  if ((event.key ?? '').toLowerCase() !== 'k' || event.shiftKey || event.altKey) {
    return false
  }
  const isMac = isApplePlatform()
  return isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey
}

export function useAppLayoutKeyboardShortcuts(params: UseAppLayoutKeyboardShortcutsParams) {
  const { prefs: userPrefs } = useUserPreferences()
  const globalSearchBarRef = ref<InstanceType<typeof GlobalSearchBar> | null>(null)

  const toolboxShortcutVisual = useShortcutKeyVisualFeedback(
    computed(() => userPrefs.value.shortcuts.toolboxToggle),
  )
  const geolocShortcutVisual = useShortcutKeyVisualFeedback(
    computed(() => userPrefs.value.shortcuts.geolocate),
  )
  const editModeShortcutVisual = useShortcutKeyVisualFeedback(
    computed(() => userPrefs.value.shortcuts.editModeToggle),
  )

  const toolboxShortcutKeys = toolboxShortcutVisual.keys
  const toolboxShortcutPressedKeys = toolboxShortcutVisual.pressedKeys
  const toolboxShortcutSublimation = toolboxShortcutVisual.sublimationTriggered
  const geolocShortcutKeys = geolocShortcutVisual.keys
  const geolocShortcutPressedKeys = geolocShortcutVisual.pressedKeys
  const geolocShortcutSublimation = geolocShortcutVisual.sublimationTriggered
  const editModeShortcutKeys = editModeShortcutVisual.keys
  const editModePressedKeys = editModeShortcutVisual.pressedKeys
  const sublimationTriggered = editModeShortcutVisual.sublimationTriggered

  function onKeydown(event: KeyboardEvent): void {
    if (params.isConnectionRoute.value) {
      return
    }
    if (shouldFocusGlobalSearchFromShortcut(event, params.loggedIn.value, params.isConnectionRoute.value)) {
      event.preventDefault()
      globalSearchBarRef.value?.focusSearchInput()
      return
    }
    editModeShortcutVisual.onKeyDown(event)
    toolboxShortcutVisual.onKeyDown(event)
    geolocShortcutVisual.onKeyDown(event)
    if (!shortcutMatchesEvent(userPrefs.value.shortcuts.editModeToggle, event)) {
      return
    }
    event.preventDefault()
    params.trySetEditMode(!params.isEditing.value)
  }

  function onGlobalKeyUp(event: KeyboardEvent): void {
    if (params.isConnectionRoute.value) {
      return
    }
    editModeShortcutVisual.onKeyUp(event)
    toolboxShortcutVisual.onKeyUp(event)
    geolocShortcutVisual.onKeyUp(event)
  }

  function onUserShortcutAction(actionId: 'toolboxToggle' | 'geolocate'): void {
    if (actionId === 'toolboxToggle') {
      params.onToolboxToggle()
      return
    }
    if (actionId === 'geolocate') {
      params.onGeolocate()
    }
  }

  useUserShortcuts({
    shortcuts: computed(() => userPrefs.value.shortcuts),
    onAction: onUserShortcutAction,
    enabled: params.toolboxShortcutsEnabled,
  })

  function registerKeyboardListeners(): void {
    document.addEventListener('keydown', onKeydown)
    document.addEventListener('keyup', onGlobalKeyUp)
  }

  function unregisterKeyboardListeners(): void {
    document.removeEventListener('keydown', onKeydown)
    document.removeEventListener('keyup', onGlobalKeyUp)
  }

  return {
    globalSearchBarRef,
    toolboxShortcutKeys,
    toolboxShortcutPressedKeys,
    toolboxShortcutSublimation,
    geolocShortcutKeys,
    geolocShortcutPressedKeys,
    geolocShortcutSublimation,
    editModeShortcutKeys,
    editModePressedKeys,
    sublimationTriggered,
    registerKeyboardListeners,
    unregisterKeyboardListeners,
  }
}
